import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import ListHeader from 'shell/components/shared/list-header';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import TypesEventFilter from 'system/components/types-event/list/filters';
import { typesEventCrumbs } from 'system/pages/type-events/constants';
import { authsObj, validationSchemaTypesEventFilterForm } from './constants';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { headCells } from './constants';
import { initialValuesTypesEvent } from 'system/pages/type-events/index';
import { TypesEventClient, typesEventFormData } from './types';
import Table from 'shell/components/shared/table';
import React, { useCallback, useMemo } from 'react';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';

import DeleteSuccessModal from 'system/components/shared/delete-success';
import { TypeEvent } from 'shell/models/type-event';
import { HttpResponseList } from 'shell/models/http-response';
import { EventResponse, transformerTypeEvents } from '../utils/transformer-type-events';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { transformDataset } from './utils';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';

const EventListPage = (): React.ReactElement => {
  const auths = useActionAuthorization(authsObj);
  const navigate = useNavigate();

  const [typeEvents, getEventLists] = useHttpFetch<HttpResponseList<TypeEvent>, EventResponse>(
    `${__OPERATION__EVENT__ADMIN__}/tipoEvento/list`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformerTypeEvents,
    }
  );

  const deleteTypeEvent = useHttpClient(`${__OPERATION__EVENT__ADMIN__}/tipoEvento`, {
    options: {
      method: 'DELETE',
    },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TypesEventClient>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TypesEventClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<typesEventFormData>();

  const onSubmit = useCallback(
    (formValues: typesEventFormData) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      getEventLists({
        params: {},
        paramsSerializer: () => queryString,
      });
      filterTypeEventForm.setFieldValue('page', 0);
    },
    [getEventLists]
  );

  const filterTypeEventForm = useFormik({
    initialValues: initialValuesTypesEvent,
    validationSchema: validationSchemaTypesEventFilterForm,
    onSubmit,
  });

  useEffect(() => {
    filterTypeEventForm.setValues(filterTable.values || initialValuesTypesEvent);
    const queryString = transformDataset(filterTable.values || initialValuesTypesEvent);
    getEventLists({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = {
        ...filterTypeEventForm.values,
        page: newPage || 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getEventLists({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeEvent>, EventResponse>
      ).then(() => {
        filterTypeEventForm.setFieldValue('page', newPage);
      });
    },
    [getEventLists, filterTypeEventForm.values, filterTypeEventForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterTypeEventForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      const queryString = transformDataset(newDataset);
      (
        getEventLists({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeEvent>, EventResponse>
      ).then(() => {
        filterTypeEventForm.setValues(newDataset);
      });
    },
    [getEventLists, filterTypeEventForm.setFieldValue, filterTypeEventForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<TypesEventClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/eventmanager/tipievento/dettaglio/:id', { id: cell.id }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TypesEventClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/eventmanager/tipievento/modifica/:id', { id: cell.id }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<TypesEventClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<TypesEventClient>;
    },
    [setDeleteAlertModal]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterTypeEventForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getEventLists({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeEvent>, EventResponse>
      ).then(() => {
        filterTypeEventForm.setFieldValue('orders', orders);
      });
    },
    [getEventLists, filterTypeEventForm.values, filterTypeEventForm.setValues]
  );

  const count: number = useMemo(() => {
    return (typeEvents.data as EventResponse)?.pageInfo?.totalCount || 0;
  }, [typeEvents.data]);

  return (
    <>
      <Breadcrumbs items={typesEventCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo Tipo/Evento"
        addBtnLabel="Aggiungi nuovo Tipo/Evento"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/eventmanager/tipievento/nuovo');
        }}
        title="Gestione Tipo/Evento"
      />
      <TypesEventFilter filterTypesEventForm={filterTypeEventForm} />
      {typeEvents.data ? (
        (typeEvents.data as EventResponse)?.typeEvents?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterTypeEventForm.values.orders}
            activeActions={auths}
            page={filterTypeEventForm.values.page}
            rowsPerPage={filterTypeEventForm.values.rowsPerPage}
            body={(typeEvents.data as EventResponse)?.typeEvents}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            isServer
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista tipo evento"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}

      <DeleteAlertModal
        title="Cancellazione Tipo/Evento"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteTypeEvent({
              url: `/${deleteAlertModal.element?.id}`,
            }) as AxiosPromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Tipo/Evento"
        message={`La cancellazione dell'evento ${deleteSuccessModal.element?.descrizione} è avvenuta con successo.`}
        onClose={() => {
          setDeleteSuccessModal({ open: false, element: null });
          const newDataset = {
            ...filterTypeEventForm.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getEventLists({
            params: {},
            paramsSerializer: () => queryString,
          });
        }}
        onOk={() => {
          setDeleteSuccessModal({ open: false, element: null });
          const newDataset = {
            ...filterTypeEventForm.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getEventLists({
            params: {},
            paramsSerializer: () => queryString,
          });
        }}
      />
    </>
  );
};

EventListPage.displayName = 'Event list page component';

export default EventListPage;
